<template>
  <div :class="['artifacts-pane', { open: isOpen }]">
    <div class="artifacts-pane-overlay" v-if="isOpen" @click="$emit('close')"></div>
    <div class="artifacts-pane-panel">
      <div class="artifacts-pane-header">
        <h3 class="artifacts-pane-title">{{ paneTitle }}</h3>
        <button class="artifacts-pane-close" @click="$emit('close')" title="Close artifacts pane">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="artifacts-pane-content">
        <!-- Loading state -->
        <div v-if="loading" class="artifacts-loading">
          <div class="progress-dots">
            <span class="progress-dot"></span>
            <span class="progress-dot"></span>
            <span class="progress-dot"></span>
          </div>
          <span class="artifacts-loading-text">Loading artifacts...</span>
        </div>

        <!-- Empty state -->
        <div v-else-if="artifacts.length === 0" class="artifacts-empty">
          <div class="artifacts-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
          </div>
          <p class="artifacts-empty-title">No artifacts yet</p>
          <p class="artifacts-empty-subtitle">{{ emptySubtitle }}</p>
        </div>

        <!-- Artifacts list -->
        <div v-else class="artifacts-list">
          <div
            v-for="artifact in artifacts"
            :key="artifact.id"
            class="artifact-item"
          >
            <!-- Image thumbnail for image artifacts -->
            <div v-if="isImageArtifact(artifact)" class="artifact-thumbnail">
              <img :src="artifact.file_path" :alt="artifact.file_name" class="artifact-thumbnail-img" />
            </div>
            <!-- Generic file type icon for non-image artifacts -->
            <div v-else class="artifact-icon">
              <component :is="getFileTypeIcon(artifact.file_type)" />
            </div>
            <div class="artifact-info">
              <div class="artifact-name" :title="artifact.file_name">{{ artifact.file_name }}</div>
              <div class="artifact-meta">
                <span class="artifact-type">{{ getFileTypeLabel(artifact.file_type) }}</span>
                <span class="artifact-separator">&middot;</span>
                <span class="artifact-size">{{ formatFileSize(artifact.file_size) }}</span>
                <span class="artifact-separator">&middot;</span>
                <span class="artifact-date">{{ formatDate(artifact.created_at) }}</span>
              </div>
            </div>
            <a
              :href="getDownloadUrl(artifact)"
              class="artifact-download-btn"
              title="Download"
              target="_blank"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { h } from 'vue';
import axios from '@/axios-config';

// File type icon components using render functions (Vue 3 runtime-only compatible)
const svgAttrs = (extra = {}) => ({
  width: '20', height: '20', viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
  ...extra,
});

const ImageIcon = {
  render() {
    return h('svg', svgAttrs(), [
      h('rect', { x: '3', y: '3', width: '18', height: '18', rx: '2', ry: '2' }),
      h('circle', { cx: '8.5', cy: '8.5', r: '1.5' }),
      h('polyline', { points: '21 15 16 10 5 21' }),
    ]);
  },
};

const PdfIcon = {
  render() {
    return h('svg', svgAttrs({ stroke: '#e74c3c' }), [
      h('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
      h('polyline', { points: '14 2 14 8 20 8' }),
      h('line', { x1: '16', y1: '13', x2: '8', y2: '13' }),
      h('line', { x1: '16', y1: '17', x2: '8', y2: '17' }),
      h('polyline', { points: '10 9 9 9 8 9' }),
    ]);
  },
};

const ExcelIcon = {
  render() {
    return h('svg', svgAttrs({ stroke: '#27ae60' }), [
      h('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
      h('polyline', { points: '14 2 14 8 20 8' }),
      h('line', { x1: '8', y1: '13', x2: '16', y2: '13' }),
      h('line', { x1: '8', y1: '17', x2: '16', y2: '17' }),
      h('line', { x1: '12', y1: '9', x2: '12', y2: '21' }),
    ]);
  },
};

const CsvIcon = {
  render() {
    return h('svg', svgAttrs({ stroke: '#2980b9' }), [
      h('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
      h('polyline', { points: '14 2 14 8 20 8' }),
      h('line', { x1: '8', y1: '13', x2: '16', y2: '13' }),
      h('line', { x1: '8', y1: '17', x2: '16', y2: '17' }),
    ]);
  },
};

const WordIcon = {
  render() {
    return h('svg', svgAttrs({ stroke: '#2b579a' }), [
      h('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
      h('polyline', { points: '14 2 14 8 20 8' }),
      h('line', { x1: '16', y1: '13', x2: '8', y2: '13' }),
      h('line', { x1: '16', y1: '17', x2: '8', y2: '17' }),
      h('line', { x1: '14', y1: '9', x2: '8', y2: '9' }),
    ]);
  },
};

const TextIcon = {
  render() {
    return h('svg', svgAttrs({ stroke: '#7f8c8d' }), [
      h('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
      h('polyline', { points: '14 2 14 8 20 8' }),
      h('line', { x1: '16', y1: '13', x2: '8', y2: '13' }),
      h('line', { x1: '14', y1: '17', x2: '8', y2: '17' }),
    ]);
  },
};

const GenericFileIcon = {
  render() {
    return h('svg', svgAttrs(), [
      h('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
      h('polyline', { points: '14 2 14 8 20 8' }),
    ]);
  },
};

export default {
  name: 'ArtifactsPane',

  components: {
    ImageIcon,
    PdfIcon,
    ExcelIcon,
    CsvIcon,
    WordIcon,
    TextIcon,
    GenericFileIcon,
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: [String, Number],
      default: null,
    },
    projectId: {
      type: [String, Number],
      default: null,
    },
    mode: {
      type: String,
      default: 'conversation', // 'conversation' or 'project'
      validator: (v) => ['conversation', 'project'].includes(v),
    },
  },

  emits: ['close'],

  data() {
    return {
      artifacts: [],
      loading: false,
    };
  },

  computed: {
    paneTitle() {
      return this.mode === 'project' ? 'Project Artifacts' : 'Artifacts';
    },
    emptySubtitle() {
      return this.mode === 'project'
        ? 'Generated images, documents, and files from all chats in this project will appear here.'
        : 'Generated images, documents, and files from this conversation will appear here.';
    },
    activeId() {
      return this.mode === 'project' ? this.projectId : this.conversationId;
    },
  },

  watch: {
    isOpen(newVal) {
      if (newVal && this.activeId) {
        this.fetchArtifacts();
      }
    },
    conversationId(newVal) {
      if (this.mode === 'conversation' && this.isOpen && newVal) {
        this.fetchArtifacts();
      } else if (this.mode === 'conversation' && !newVal) {
        this.artifacts = [];
      }
    },
    projectId(newVal) {
      if (this.mode === 'project' && this.isOpen && newVal) {
        this.fetchArtifacts();
      } else if (this.mode === 'project' && !newVal) {
        this.artifacts = [];
      }
    },
    mode() {
      if (this.isOpen && this.activeId) {
        this.fetchArtifacts();
      }
    },
  },

  methods: {
    async fetchArtifacts() {
      const id = this.activeId;
      if (!id) {
        this.artifacts = [];
        return;
      }
      this.loading = true;
      try {
        let url;
        if (this.mode === 'project') {
          url = `/api/projects/${id}/artifacts/`;
        } else {
          url = `/api/conversations/${id}/artifacts/`;
        }
        const response = await axios.get(url, { withCredentials: true });
        // Sort by created_at descending (newest first)
        const data = Array.isArray(response.data) ? response.data : [];
        data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        this.artifacts = data;
      } catch (error) {
        console.error('Error fetching artifacts:', error);
        this.artifacts = [];
      } finally {
        this.loading = false;
      }
    },

    isImageArtifact(artifact) {
      if (!artifact.file_type) return false;
      const ft = artifact.file_type.toLowerCase();
      return ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'image'].includes(ft);
    },

    getFileTypeIcon(fileType) {
      if (!fileType) return 'GenericFileIcon';
      const ft = fileType.toLowerCase();
      if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'image'].includes(ft)) return 'ImageIcon';
      if (['pdf'].includes(ft)) return 'PdfIcon';
      if (['xlsx', 'xls', 'excel'].includes(ft)) return 'ExcelIcon';
      if (['csv'].includes(ft)) return 'CsvIcon';
      if (['docx', 'doc'].includes(ft)) return 'WordIcon';
      if (['txt', 'text', 'md', 'log'].includes(ft)) return 'TextIcon';
      return 'GenericFileIcon';
    },

    getFileTypeLabel(fileType) {
      if (!fileType) return 'File';
      const ft = fileType.toLowerCase();
      if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'image'].includes(ft)) return 'Image';
      if (['pdf'].includes(ft)) return 'PDF';
      if (['xlsx', 'xls', 'excel'].includes(ft)) return 'Excel';
      if (['csv'].includes(ft)) return 'CSV';
      if (['docx', 'doc'].includes(ft)) return 'Word';
      if (['txt'].includes(ft)) return 'Text';
      return ft.toUpperCase();
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      return date.toLocaleDateString();
    },

    getDownloadUrl(artifact) {
      return `/api/artifacts/${artifact.id}/download/`;
    },
  },
};
</script>

<style>
.artifacts-pane {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  pointer-events: none;
}

.artifacts-pane.open {
  pointer-events: auto;
}

.artifacts-pane-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.15);
  z-index: 100;
  animation: fadeIn 0.2s ease;
}

.artifacts-pane-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 340px;
  background: #ffffff;
  border-left: 1px solid #e0e0e0;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.08);
  z-index: 101;
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.artifacts-pane.open .artifacts-pane-panel {
  transform: translateX(0);
}

.artifacts-pane-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.artifacts-pane-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  font-family: Inter, sans-serif;
  color: #1C1C1C;
}

.artifacts-pane-close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s, color 0.15s;
}

.artifacts-pane-close:hover {
  background: #f0f0f0;
  color: #333;
}

.artifacts-pane-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

/* Loading state */
.artifacts-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 12px;
}

.artifacts-loading .progress-dots {
  display: flex;
  gap: 4px;
  align-items: center;
}

.artifacts-loading .progress-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #888;
  animation: progress-bounce 1.4s ease-in-out infinite both;
}

.artifacts-loading .progress-dot:nth-child(2) {
  animation-delay: 0.16s;
}

.artifacts-loading .progress-dot:nth-child(3) {
  animation-delay: 0.32s;
}

.artifacts-loading-text {
  font-size: 13px;
  color: #888;
  font-style: italic;
}

/* Empty state */
.artifacts-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}

.artifacts-empty-icon {
  margin-bottom: 16px;
  color: #ccc;
}

.artifacts-empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #666;
  margin: 0 0 8px 0;
}

.artifacts-empty-subtitle {
  font-size: 13px;
  color: #999;
  margin: 0;
  line-height: 1.5;
}

/* Artifacts list */
.artifacts-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.artifact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f7f9fb;
  border: 1px solid #eee;
  transition: background-color 0.15s, border-color 0.15s;
}

.artifact-item:hover {
  background: #eef2f7;
  border-color: #ddd;
}

.artifact-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  color: #666;
}

.artifact-thumbnail {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  background: #f0f0f0;
}

.artifact-thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.artifact-info {
  flex: 1;
  min-width: 0;
}

.artifact-name {
  font-size: 13px;
  font-weight: 500;
  color: #1C1C1C;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.artifact-meta {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.artifact-separator {
  color: #ccc;
}

.artifact-download-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #666;
  text-decoration: none;
  transition: background-color 0.15s, color 0.15s;
}

.artifact-download-btn:hover {
  background: #e0e4e8;
  color: #333;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes progress-bounce {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
